/**
 * On-chain GBUX SPL → play-ledger funding (production mainnet).
 *
 * Web3 dApp practice:
 * 1. User connects injected wallet (Phantom / Solflare / …)
 * 2. App builds SPL transfer to BUDBai deposit agent
 * 3. Wallet prompts **transaction signature** (not just message)
 * 4. Tx confirms on Solana mainnet
 * 5. Edge verifies signature + credits D1 play ledger 1:1
 *
 * Poker / slots / blackjack never debit on-chain balances — only play ledger.
 */
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  LAMPORTS_PER_SOL,
  type TransactionInstruction,
} from "@solana/web3.js";
import {
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  getAssociatedTokenAddressSync,
  getAccount,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { GBUX } from "@shared/fleet";
import { GBUX_DECIMALS, toGbuxRaw } from "@shared/gbux-math";
import type { ConnectedSolanaWallet } from "./solana-wallets";

const BUDBai_DEFAULT = "6P7Pp5eHzPAVjnbNLkW8DzAuuc7gj9Sm5XiprwnjzvRs";
/** Mainnet USDT mint — purchases settle to agent, play GBUX credited server-side */
const USDT_MINT = GBUX.usdtMint || "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB";
const USDT_DECIMALS = 6;

/**
 * Balance reads: prefer private VITE_SOLANA_RPC only.
 * Public mainnet RPC 403s under browser load — use edge `/api/wallet/scopes` instead.
 */
export function getRpcUrl(): string | null {
  const envRpc = (import.meta as { env?: { VITE_SOLANA_RPC?: string } }).env
    ?.VITE_SOLANA_RPC;
  if (!envRpc || !envRpc.trim()) return null;
  if (/devnet|testnet|localhost/i.test(envRpc)) {
    console.warn("[gbux-transfer] refusing non-mainnet RPC", envRpc);
    return null;
  }
  return envRpc.trim();
}

/** Sign/send txs need an RPC; private preferred, public mainnet last resort. */
export function getTxRpcUrl(): string {
  return getRpcUrl() || GBUX.rpcDefault || "https://api.mainnet-beta.solana.com";
}

export function explorerTxUrl(sig: string): string {
  return `https://solscan.io/tx/${sig}`;
}

export async function fetchOnChainGbux(
  ownerAddress: string,
  rpc: string | null = getRpcUrl(),
): Promise<number> {
  if (!rpc) return 0;
  try {
    const connection = new Connection(rpc, "confirmed");
    const owner = new PublicKey(ownerAddress);
    const mint = new PublicKey(GBUX.mint);
    const ata = getAssociatedTokenAddressSync(mint, owner);
    const acc = await getAccount(connection, ata);
    return Number(acc.amount) / 10 ** GBUX_DECIMALS;
  } catch {
    return 0;
  }
}

export async function fetchOnChainSol(
  ownerAddress: string,
  rpc: string | null = getRpcUrl(),
): Promise<number> {
  if (!rpc) return 0;
  try {
    const connection = new Connection(rpc, "confirmed");
    const lamports = await connection.getBalance(new PublicKey(ownerAddress));
    return lamports / 1e9;
  } catch {
    return 0;
  }
}

/**
 * Sign + send GBUX SPL from connected wallet → BUDBai deposit agent.
 * Wallet extension shows the real transfer request (amount + mint + destination).
 */
export async function transferGbuxToPlayAgent(params: {
  wallet: ConnectedSolanaWallet;
  amountHuman: number;
  depositTo?: string;
  rpc?: string;
}): Promise<{ signature: string; amount: number; depositTo: string }> {
  const amount = Math.floor(params.amountHuman);
  if (!Number.isFinite(amount) || amount < 1) {
    throw new Error("Minimum transfer is 1 GBUX");
  }

  const depositTo = (params.depositTo || BUDBai_DEFAULT).trim();
  if (depositTo.length < 32) throw new Error("Invalid deposit agent address");

  const rpc = params.rpc || getTxRpcUrl();
  const connection = new Connection(rpc, "confirmed");
  const from = new PublicKey(params.wallet.publicKey);
  const to = new PublicKey(depositTo);
  const mint = new PublicKey(GBUX.mint);
  const raw = toGbuxRaw(amount, GBUX_DECIMALS);

  const fromAta = getAssociatedTokenAddressSync(mint, from);
  const toAta = getAssociatedTokenAddressSync(mint, to);

  let fromBal = BigInt(0);
  try {
    const acc = await getAccount(connection, fromAta);
    fromBal = acc.amount;
  } catch {
    throw new Error(
      "No GBUX token account in this wallet — receive or buy GBUX on mainnet first",
    );
  }
  if (fromBal < raw) {
    const have = Number(fromBal) / 10 ** GBUX_DECIMALS;
    throw new Error(
      `Insufficient on-chain GBUX (have ${have.toLocaleString()}, need ${amount})`,
    );
  }

  // Need ~0.002 SOL for ATA rent if agent ATA missing + fees
  const solLamports = await connection.getBalance(from);
  if (solLamports < 5_000) {
    throw new Error("Need a little SOL for network fees (mainnet)");
  }

  const ixs: TransactionInstruction[] = [];
  try {
    await getAccount(connection, toAta);
  } catch {
    ixs.push(
      createAssociatedTokenAccountInstruction(
        from,
        toAta,
        to,
        mint,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID,
      ),
    );
  }

  ixs.push(
    createTransferInstruction(
      fromAta,
      toAta,
      from,
      raw,
      [],
      TOKEN_PROGRAM_ID,
    ),
  );

  const feePayerInfo = await fetchFeePayerInfo();
  const feePayerPk =
    feePayerInfo?.enabled && feePayerInfo.publicKey
      ? new PublicKey(feePayerInfo.publicKey)
      : from;

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction({
    feePayer: feePayerPk,
    blockhash,
    lastValidBlockHeight,
  }).add(...ixs);

  const signature = await signAndSendWithWallet(params.wallet, tx, connection, {
    useDappFeePayer: feePayerPk.equals(from) === false,
  });

  // Confirm (wallet may return before finality)
  try {
    await connection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      "confirmed",
    );
  } catch {
    // Still return signature — edge deposit verify re-checks chain
  }

  return { signature, amount, depositTo };
}

export async function fetchFeePayerInfo(): Promise<{
  enabled: boolean;
  publicKey: string | null;
  platformFee: number;
} | null> {
  try {
    const r = await fetch("/api/wallet/fee-payer");
    if (!r.ok) return null;
    return (await r.json()) as {
      enabled: boolean;
      publicKey: string | null;
      platformFee: number;
    };
  } catch {
    return null;
  }
}

/** Phantom presignTransaction → edge co-sign as dapp fee payer */
export async function presignWithDappFeePayer(
  transaction: string,
  context: { networkId: string; walletId: string },
): Promise<string> {
  const r = await fetch("/api/wallet/presign-tx", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      transaction,
      networkId: context.networkId,
      walletId: context.walletId,
    }),
  });
  const j = (await r.json()) as { transaction?: string; error?: string };
  if (!r.ok || !j.transaction) {
    throw new Error(j.error || "Dapp fee-payer presign failed");
  }
  return j.transaction;
}

async function signAndSendWithWallet(
  wallet: ConnectedSolanaWallet,
  tx: Transaction,
  connection: Connection,
  flags?: { useDappFeePayer?: boolean },
): Promise<string> {
  const provider = wallet.provider;
  const opts: {
    skipPreflight: boolean;
    preflightCommitment: "confirmed";
    maxRetries: number;
    presignTransaction?: (
      t: string,
      ctx: { networkId: string; walletId: string },
    ) => Promise<string>;
  } = {
    skipPreflight: false,
    preflightCommitment: "confirmed",
    maxRetries: 3,
  };

  // Phantom embedded fee-payer: backend partial-signs, then user signs
  if (flags?.useDappFeePayer) {
    opts.presignTransaction = presignWithDappFeePayer;
  }

  // 1) Standard injected / Phantom SDK: signAndSendTransaction
  if (typeof provider.signAndSendTransaction === "function") {
    try {
      const res = await provider.signAndSendTransaction(tx, opts);
      const sig = typeof res === "string" ? res : res?.signature;
      if (sig && typeof sig === "string" && sig.length >= 40) return sig;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/reject|denied|cancel/i.test(msg)) {
        throw new Error("Signature request rejected in wallet");
      }
      // fall through to signTransaction
    }
  }

  // 2) Sign only + app broadcast
  if (typeof provider.signTransaction === "function") {
    try {
      const signed = (await provider.signTransaction(tx)) as Transaction;
      const raw = signed.serialize();
      const sig = await connection.sendRawTransaction(raw, {
        skipPreflight: false,
        maxRetries: 3,
      });
      if (sig) return sig;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/reject|denied|cancel/i.test(msg)) {
        throw new Error("Signature request rejected in wallet");
      }
      throw new Error(msg || "Wallet signTransaction failed");
    }
  }

  // 3) Phantom-style request RPC
  if (typeof provider.request === "function") {
    try {
      const res = (await provider.request({
        method: "signAndSendTransaction",
        params: {
          message: tx.serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          }),
        },
      })) as { signature?: string } | string;
      const sig = typeof res === "string" ? res : res?.signature;
      if (sig && sig.length >= 40) return sig;
    } catch {
      /* ignore */
    }
  }

  throw new Error(
    "Wallet cannot sign transactions — open Phantom/Solflare, unlock, and try Transfer again",
  );
}

/**
 * After chain transfer confirms, credit play ledger 1:1 via edge verification.
 * Idempotent on server (same tx signature cannot double-credit).
 */
export async function creditPlayFromTx(
  wallet: string,
  txSignature: string,
): Promise<{ gbuxCredited: number; playGbux: number; status?: string }> {
  const r = await fetch("/api/play/deposit-gbux", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ wallet, txSignature }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Play credit failed");
  return {
    gbuxCredited: Number(data.gbuxCredited) || 0,
    playGbux: Number(data.playGbux) || 0,
    status: data.status,
  };
}

/**
 * Full path: sign transfer → wait briefly → credit play.
 * Retries credit a few times while RPC indexes the tx.
 */
export async function transferAndCreditPlay(params: {
  wallet: ConnectedSolanaWallet;
  amountHuman: number;
  depositTo?: string;
}): Promise<{
  signature: string;
  amount: number;
  playGbux: number;
  gbuxCredited: number;
}> {
  const { signature, amount, depositTo } = await transferGbuxToPlayAgent(params);

  let lastErr: Error | null = null;
  for (let i = 0; i < 5; i++) {
    try {
      if (i > 0) await sleep(1500 * i);
      const credit = await creditPlayFromTx(params.wallet.publicKey, signature);
      return {
        signature,
        amount,
        playGbux: credit.playGbux,
        gbuxCredited: credit.gbuxCredited || amount,
      };
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
    }
  }
  throw new Error(
    lastErr?.message
      ? `${lastErr.message} · tx ${signature.slice(0, 12)}… (paste signature under Verify if credit lags)`
      : `Transfer sent but play credit pending — paste ${signature.slice(0, 16)}…`,
  );
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Native SOL → BUDBai sales agent (purchase proceeds stay on agent).
 * Server credits play GBUX from verified SOL amount @ fleet rate.
 */
export async function transferSolToPlayAgent(params: {
  wallet: ConnectedSolanaWallet;
  /** SOL amount (human) */
  amountSol: number;
  depositTo?: string;
}): Promise<{ signature: string; amountSol: number; depositTo: string }> {
  const amountSol = Number(params.amountSol);
  if (!Number.isFinite(amountSol) || amountSol <= 0) {
    throw new Error("Enter SOL amount > 0");
  }
  const depositTo = (params.depositTo || BUDBai_DEFAULT).trim();
  const connection = new Connection(getTxRpcUrl(), "confirmed");
  const from = new PublicKey(params.wallet.publicKey);
  const to = new PublicKey(depositTo);
  const lamports = Math.floor(amountSol * LAMPORTS_PER_SOL);
  if (lamports < 1000) throw new Error("SOL amount too small");

  const bal = await connection.getBalance(from);
  // keep ~0.002 SOL for fees
  if (bal < lamports + 2_000_000) {
    throw new Error(
      `Insufficient SOL (need ~${amountSol + 0.002} including fees)`,
    );
  }

  const feePayerInfo = await fetchFeePayerInfo();
  const feePayerPk =
    feePayerInfo?.enabled && feePayerInfo.publicKey
      ? new PublicKey(feePayerInfo.publicKey)
      : from;

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction({
    feePayer: feePayerPk,
    blockhash,
    lastValidBlockHeight,
  }).add(
    SystemProgram.transfer({
      fromPubkey: from,
      toPubkey: to,
      lamports,
    }),
  );

  const signature = await signAndSendWithWallet(params.wallet, tx, connection, {
    useDappFeePayer: !feePayerPk.equals(from),
  });
  try {
    await connection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      "confirmed",
    );
  } catch {
    /* credit path re-verifies */
  }
  return { signature, amountSol, depositTo };
}

/**
 * USDT SPL → BUDBai (purchase proceeds on agent). Play GBUX via /api/sales/buy-gbux.
 */
export async function transferUsdtToPlayAgent(params: {
  wallet: ConnectedSolanaWallet;
  amountUsdt: number;
  depositTo?: string;
}): Promise<{ signature: string; amountUsdt: number; depositTo: string }> {
  const amount = Math.floor(Number(params.amountUsdt) * 10 ** USDT_DECIMALS) /
    10 ** USDT_DECIMALS;
  if (!Number.isFinite(amount) || amount < 0.01) {
    throw new Error("Minimum USDT transfer is 0.01");
  }
  const depositTo = (params.depositTo || BUDBai_DEFAULT).trim();
  const connection = new Connection(getTxRpcUrl(), "confirmed");
  const from = new PublicKey(params.wallet.publicKey);
  const to = new PublicKey(depositTo);
  const mint = new PublicKey(USDT_MINT);
  const raw = BigInt(Math.round(amount * 10 ** USDT_DECIMALS));

  const fromAta = getAssociatedTokenAddressSync(mint, from);
  const toAta = getAssociatedTokenAddressSync(mint, to);

  let fromBal = BigInt(0);
  try {
    fromBal = (await getAccount(connection, fromAta)).amount;
  } catch {
    throw new Error("No USDT token account — buy USDT on Solana mainnet first");
  }
  if (fromBal < raw) {
    throw new Error(
      `Insufficient USDT (have ${Number(fromBal) / 10 ** USDT_DECIMALS})`,
    );
  }

  const ixs: TransactionInstruction[] = [];
  try {
    await getAccount(connection, toAta);
  } catch {
    ixs.push(
      createAssociatedTokenAccountInstruction(
        from,
        toAta,
        to,
        mint,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID,
      ),
    );
  }
  ixs.push(
    createTransferInstruction(fromAta, toAta, from, raw, [], TOKEN_PROGRAM_ID),
  );

  const feePayerInfo = await fetchFeePayerInfo();
  const feePayerPk =
    feePayerInfo?.enabled && feePayerInfo.publicKey
      ? new PublicKey(feePayerInfo.publicKey)
      : from;

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction({
    feePayer: feePayerPk,
    blockhash,
    lastValidBlockHeight,
  }).add(...ixs);

  const signature = await signAndSendWithWallet(params.wallet, tx, connection, {
    useDappFeePayer: !feePayerPk.equals(from),
  });
  try {
    await connection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      "confirmed",
    );
  } catch {
    /* ignore */
  }
  return { signature, amountUsdt: amount, depositTo };
}

/** Credit play from verified SOL/USDT purchase (payment stays on agent). */
export async function creditPlayFromPurchase(params: {
  wallet: string;
  method: "SOL" | "USDT";
  txSignature: string;
}): Promise<{ gbuxCredited: number; playGbux: number; status?: string }> {
  const r = await fetch("/api/sales/buy-gbux", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      wallet: params.wallet,
      method: params.method,
      txSignature: params.txSignature,
    }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Purchase credit failed");
  return {
    gbuxCredited: Number(data.gbuxCredited) || 0,
    playGbux: Number(data.playGbux) || 0,
    status: data.status,
  };
}

export async function purchaseSolAndCreditPlay(params: {
  wallet: ConnectedSolanaWallet;
  amountSol: number;
  depositTo?: string;
}): Promise<{
  signature: string;
  playGbux: number;
  gbuxCredited: number;
}> {
  const { signature } = await transferSolToPlayAgent(params);
  let lastErr: Error | null = null;
  for (let i = 0; i < 5; i++) {
    try {
      if (i > 0) await sleep(1500 * i);
      const credit = await creditPlayFromPurchase({
        wallet: params.wallet.publicKey,
        method: "SOL",
        txSignature: signature,
      });
      return {
        signature,
        playGbux: credit.playGbux,
        gbuxCredited: credit.gbuxCredited,
      };
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
    }
  }
  throw new Error(
    lastErr?.message
      ? `${lastErr.message} · tx ${signature.slice(0, 12)}…`
      : "SOL sent; play credit pending — paste signature",
  );
}

export async function purchaseUsdtAndCreditPlay(params: {
  wallet: ConnectedSolanaWallet;
  amountUsdt: number;
  depositTo?: string;
}): Promise<{
  signature: string;
  playGbux: number;
  gbuxCredited: number;
}> {
  const { signature } = await transferUsdtToPlayAgent(params);
  let lastErr: Error | null = null;
  for (let i = 0; i < 5; i++) {
    try {
      if (i > 0) await sleep(1500 * i);
      const credit = await creditPlayFromPurchase({
        wallet: params.wallet.publicKey,
        method: "USDT",
        txSignature: signature,
      });
      return {
        signature,
        playGbux: credit.playGbux,
        gbuxCredited: credit.gbuxCredited,
      };
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
    }
  }
  throw new Error(
    lastErr?.message
      ? `${lastErr.message} · tx ${signature.slice(0, 12)}…`
      : "USDT sent; play credit pending — paste signature",
  );
}

export const PLAY_DEPOSIT_SSOT = {
  agent: BUDBai_DEFAULT,
  mint: GBUX.mint,
  usdtMint: USDT_MINT,
  network: "mainnet-beta" as const,
  path: "/api/play/deposit-gbux",
  purchasePath: "/api/sales/buy-gbux",
  walletDashboard: "https://wallet.grudge-studio.com",
  /**
   * Money / pricing contract (no on-chain program required for Phantom dApps):
   * User signs SOL/SPL transfers to agent; edge verifies + credits play ledger.
   * Phantom: sdk.solana.signAndSendTransaction only — user approves each tx.
   */
  moneyContract: {
    rate: "1 GBUX = $0.001 USD",
    gbuxDeposit: "SPL GBUX → agent → play 1:1",
    solPurchase:
      "SOL → agent → play GBUX = floor(sol × solUsd × 0.95 / 0.001)",
    usdtPurchase: "USDT → agent → play GBUX = floor(usdt × 0.95 / 0.001)",
    purchaseFee: 0.05,
    dappFeePayer: "POST /api/wallet/presign-tx (Phantom presignTransaction)",
    phantomDomain: "poker.grudge-studio.com",
  },
} as const;
